import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// --- SVG ICONS ---
const InstagramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85s-.011 3.584-.069 4.85c-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07s-3.584-.012-4.85-.07c-3.252-.148-4.771-1.691-4.919-4.919-.058-1.265-.069-1.645-.069-4.85s.011-3.584.069-4.85c.149-3.225 1.664-4.771 4.919 4.919 1.266-.057 1.644-.07 4.85-.07zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948s.014 3.667.072 4.947c.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072s3.667-.014 4.947-.072c4.358-.2 6.78-2.618 6.98-6.98.059-1.281.073-1.689.073-4.948s-.014-3.667-.072-4.947c-.2-4.358-2.618-6.78-6.98-6.98-1.281-.058-1.689-.072-4.948-.072zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.162 6.162 6.162 6.162-2.759 6.162-6.162-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4s1.791-4 4-4 4 1.79 4 4-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.441 1.441 1.441 1.441-.645 1.441-1.441-.645-1.44-1.441-1.44z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
    </svg>
);


const Footer = () => {
    const navigate = useNavigate();

    const footerLinks = {
        'Navigation': [
            { label: 'Home', path: '/' },
            { label: 'Search', path: '/search' },
            { label: 'Submit Review', path: '/submit' },
            { label: 'My Account', path: '/account' },
        ],
        'About': [
            { label: 'Our Mission', path: '/' },
            { label: 'Contact Us', path: '/' },
            { label: 'FAQs', path: '/' }
        ],
        'Legal': [
            { label: 'Privacy Policy', path: '/privacy' },
            { label: 'Terms of Service', path: '/terms' }
        ]
    };

    // --- FADE-IN ANIMATION LOGIC ---
    const footerRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (footerRef.current) {
            observer.observe(footerRef.current);
        }

        return () => {
            if (footerRef.current) {
                observer.unobserve(footerRef.current);
            }
        };
    }, []);

    return (
        <>
            {/* --- CSS STYLES --- */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .footer {
                    font-family: 'Inter', sans-serif;
                    background-color: #111827;
                    color: #9ca3af;
                    padding: 5rem 1.5rem 2rem 1.5rem;
                }
                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                /* Fade-in Animation */
                .fade-in-section {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
                }
                .fade-in-section.visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                .footer-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 2.5rem;
                }
                .footer-brand {
                    grid-column: span 1;
                }
                .footer-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: white;
                    margin-bottom: 1rem;
                }
                .footer-tagline {
                    line-height: 1.6;
                }
                .social-links {
                    margin-top: 1.5rem;
                    display: flex;
                    gap: 1rem;
                }
                .social-icon {
                    color: #9ca3af;
                    transition: color 0.3s, transform 0.3s;
                }
                .social-icon:hover {
                    color: white;
                    transform: translateY(-2px);
                }
                
                .footer-links-col h4 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #d1d5db;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 1.25rem;
                }
                .footer-link {
                    display: block;
                    margin-bottom: 0.75rem;
                    color: #9ca3af;
                    text-decoration: none;
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    text-align: left;
                    font-size: 1rem;
                    transition: color 0.3s;
                }
                .footer-link:hover {
                    color: white;
                }

                .footer-bottom {
                    text-align: center;
                    margin-top: 4rem;
                    padding-top: 2rem;
                    border-top: 1px solid #374151;
                    font-size: 0.9rem;
                }

                @media (max-width: 900px) {
                    .footer-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .footer-brand {
                        grid-column: span 2;
                    }
                }
                @media (max-width: 640px) {
                    .footer-grid {
                        grid-template-columns: 1fr;
                    }
                    .footer-brand {
                        grid-column: span 1;
                    }
                }
            `}</style>

            {/* --- JSX STRUCTURE --- */}
            <footer className="footer fade-in-section" ref={footerRef}>
                <div className="container">
                    <div className="footer-grid">
                        <div className="footer-brand">
                            <h3 className="footer-title">RentRadar</h3>
                            <p className="footer-tagline">Transparent rental costs for the Clifton & UC community.</p>
                            <div className="social-links">
                                <a href="https://www.instagram.com/rent.radar_cincy" target="_blank" rel="noopener noreferrer" className="social-icon"><InstagramIcon /></a>
                                <a href="https://www.linkedin.com/company/rent-radar-cincy" target="_blank" rel="noopener noreferrer" className="social-icon"><LinkedInIcon /></a>
                            </div>
                        </div>

                        {Object.entries(footerLinks).map(([title, links]) => (
                            <div className="footer-links-col" key={title}>
                                <h4>{title}</h4>
                                {links.map(link => (
                                    <button
                                        key={link.label}
                                        onClick={() => navigate(link.path)}
                                        className="footer-link"
                                    >
                                        {link.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="footer-bottom">
                        <p>&copy; {new Date().getFullYear()} RentRadar. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer;
